/** Pure, versioned racquet scoring. No clock, I/O, display position or spoken names. */
export const RULES_VERSION = 1
const fail = (code) => {
  throw Object.assign(new Error(code), { code, statusCode: 400 })
}
const pair = (value) =>
  Array.isArray(value) &&
  value.length === 2 &&
  value.every((n) => Number.isSafeInteger(n) && n >= 0 && n <= 1000)
const copy = (value) => structuredClone(value)
const base = (sport, id, bestOf, extra = {}) =>
  Object.freeze({
    version: RULES_VERSION,
    sport,
    id,
    bestOf,
    winBy: 2,
    ...extra
  })
export const RACQUET_PRESETS = Object.freeze({
  tennis_one_set: base('tennis', 'tennis_one_set', 1, {
    deuce: 'advantage',
    gamesTo: 6,
    tieBreakAt: 6,
    tieBreakTo: 7
  }),
  tennis_best_of_three: base('tennis', 'tennis_best_of_three', 3, {
    deuce: 'advantage',
    gamesTo: 6,
    tieBreakAt: 6,
    tieBreakTo: 7
  }),
  padel_advantage: base('padel', 'padel_advantage', 3, {
    deuce: 'advantage',
    gamesTo: 6,
    tieBreakAt: 6,
    tieBreakTo: 7
  }),
  pickleball_one_game: base('pickleball', 'pickleball_one_game', 1, {
    scoring: 'side_out',
    pointsTo: 11
  }),
  pickleball_best_of_three: base('pickleball', 'pickleball_best_of_three', 3, {
    scoring: 'side_out',
    pointsTo: 11
  }),
  squash_best_of_three: base('squash', 'squash_best_of_three', 3, {
    scoring: 'point_a_rally',
    pointsTo: 11
  }),
  squash_best_of_five: base('squash', 'squash_best_of_five', 5, {
    scoring: 'point_a_rally',
    pointsTo: 11
  })
})
export const RACQUET_SOURCES = Object.freeze({
  tennis: {
    body: 'ITF',
    edition: '2026',
    url: 'https://www.itftennis.com/media/7221/2026-rules-of-tennis-english.pdf',
    sections: '5–7, 14–17',
    checked: '2026-10-14'
  },
  padel: {
    body: 'FIP',
    edition: 'Effective 2026-01-01',
    url: 'https://www.padelfip.com/wp-content/uploads/2025/12/FIP_Rules-of-Padel-1.pdf',
    sections: '1 option 1, tie-break; 6, 8',
    checked: '2026-10-14'
  },
  pickleball: {
    body: 'USA Pickleball',
    edition: '2026 official rulebook (publisher edition 2026-06-12)',
    url: 'https://fliphtml5.com/cksih/USAP-Official-Rulebook/',
    sections: '4, 5, 15.C.1 (friendly scoring presets)',
    checked: '2026-10-14'
  },
  squash: {
    body: 'World Squash',
    edition: '2025 V1.2 effective 2025-09-01',
    url: 'https://worldsquashofficiating.com/wp-content/uploads/2025/08/250901_World-Squash-Rules-of-Singles-Squash-2025-V1.2.2.pdf',
    sections: '2, 5',
    checked: '2026-10-14'
  }
})
export function rulesSnapshot(id) {
  if (!Object.hasOwn(RACQUET_PRESETS, id)) fail('unsupported_rules')
  return {
    ...copy(RACQUET_PRESETS[id]),
    source: copy(RACQUET_SOURCES[RACQUET_PRESETS[id].sport])
  }
}
// Version 1 presets remain immutable. Version 2 changes session lifecycle only.
export function casualRulesSnapshot(sport) {
  const id = {
    tennis: 'tennis_one_set',
    padel: 'padel_advantage',
    pickleball: 'pickleball_one_game',
    squash: 'squash_best_of_three'
  }[sport]
  if (!id) fail('unsupported_rules')
  return {
    ...rulesSnapshot(id),
    version: 2,
    id: `${sport}_casual`,
    bestOf: null,
    sessionMode: 'casual'
  }
}
export const isCasual = (s) =>
  s.rules.version === 2 && s.rules.sessionMode === 'casual'
export function validateRules(rules) {
  if (rules?.version === 2) {
    if (rules.sessionMode !== 'casual') fail('unsupported_rules_version')
    const canonical = casualRulesSnapshot(rules.sport)
    if (
      JSON.stringify(Object.keys(rules).sort()) !==
        JSON.stringify(Object.keys(canonical).sort()) ||
      Object.entries(canonical).some(([k, v]) =>
        k === 'source'
          ? !rules.source ||
            Object.keys(rules.source).length !== Object.keys(v).length ||
            Object.entries(v).some(
              ([field, value]) => rules.source[field] !== value
            )
          : rules[k] !== v
      )
    )
      fail('invalid_rules_snapshot')
    return rules
  }
  if (
    !rules ||
    rules.version !== RULES_VERSION ||
    !Object.hasOwn(RACQUET_PRESETS, rules.id)
  )
    fail('unsupported_rules_version')
  const canonical = RACQUET_PRESETS[rules.id]
  const { source, ...scoring } = rules
  if (
    source &&
    Object.entries(RACQUET_SOURCES[canonical.sport]).some(
      ([k, v]) => source[k] !== v
    )
  )
    fail('invalid_rules_source')
  if (
    Object.keys(scoring).length !== Object.keys(canonical).length ||
    Object.entries(canonical).some(([k, v]) => scoring[k] !== v)
  )
    fail('invalid_rules_snapshot')
  return rules
}
export function createRacquetState({
  rules,
  teams,
  serviceOrder,
  initialServer,
  initialBox = 'right'
}) {
  validateRules(rules)
  if (
    !Array.isArray(teams) ||
    teams.length !== 2 ||
    teams.some(
      (t) => typeof t.id !== 'string' || !t.id || !Array.isArray(t.players)
    )
  )
    fail('invalid_teams')
  const count = teams[0].players.length
  if (
    ![1, 2].includes(count) ||
    teams[1].players.length !== count ||
    (rules.sport === 'padel' && count !== 2) ||
    (rules.sport === 'squash' && count !== 1)
  )
    fail('invalid_format')
  const players = teams.flatMap((t) => t.players)
  const unknownService =
    rules.version === 2 && initialServer == null && serviceOrder == null
  if (unknownService) {
    const seed = createRacquetState({
      rules,
      teams,
      serviceOrder: teams[0].players.flatMap((p, i) => [
        p,
        teams[1].players[i]
      ]),
      initialServer: players[0],
      initialBox
    })
    return {
      ...seed,
      serviceOrder: [],
      server: null,
      gameInitialServer: null,
      serviceBox: null,
      positions: null,
      serviceKnown: false,
      closure: 'open',
      sportingCompletion: false
    }
  }
  if (
    teams[0].id === teams[1].id ||
    players.some((p) => typeof p !== 'string' || !p) ||
    new Set(players).size !== players.length
  )
    fail('duplicate_identity')
  if (
    !Array.isArray(serviceOrder) ||
    serviceOrder.length !== players.length ||
    new Set(serviceOrder).size !== players.length ||
    serviceOrder.some((p) => !players.includes(p))
  )
    fail('invalid_service_order')
  // Tennis and padel service order must alternate teams.
  if (
    ['tennis', 'padel'].includes(rules.sport) &&
    serviceOrder.some(
      (p, i) =>
        teamOf({ teams }, p) ===
        teamOf({ teams }, serviceOrder[(i + 1) % players.length])
    )
  )
    fail('invalid_service_order')
  if (
    !players.includes(initialServer) ||
    !['left', 'right'].includes(initialBox)
  )
    fail('initial_service_required')
  if (rules.sport !== 'squash' && initialBox !== 'right')
    fail('invalid_initial_service_box')
  const positions = teams.map((t) =>
    count === 1 ? [t.players[0], null] : [t.players[0], t.players[1]]
  )
  if (
    rules.sport === 'pickleball' &&
    count === 2 &&
    positions[teamOf({ teams }, initialServer)][0] !== initialServer
  )
    positions[teamOf({ teams }, initialServer)].reverse()
  return {
    version: rules.version,
    ...(rules.version === 2
      ? { serviceKnown: true, closure: 'open', sportingCompletion: false }
      : {}),
    rules: copy(rules),
    teams: copy(teams),
    serviceOrder: [...serviceOrder],
    points: [0, 0],
    games: [0, 0],
    sets: [0, 0],
    segments: [],
    status: 'in_progress',
    winner: null,
    server: initialServer,
    serverNumber: rules.sport === 'pickleball' && count === 2 ? 2 : 1,
    serviceBox: initialBox,
    gameInitialServer: initialServer,
    tieBreak: false,
    tieBreakInitialServer: null,
    // Pickleball positions are identities in right/left courts, independent of display sides.
    positions,
    provenance: 'rally',
    summaryOnly: false
  }
}
const teamOf = (s, player) =>
  s.teams.findIndex((t) => t.players.includes(player))
const nextServer = (s, server, n = 1) =>
  s.serviceOrder[(s.serviceOrder.indexOf(server) + n) % s.serviceOrder.length]
const won = (scores, i, target, margin = 2) =>
  scores[i] >= target && scores[i] - scores[1 - i] >= margin
function finishSegment(s, i, score, kind) {
  s.segments.push({
    kind,
    ...(isCasual(s) ? { completed: true } : {}),
    score: [...score],
    winner: s.teams[i].id,
    provenance: s.provenance
  })
  const totals = kind === 'set' ? s.sets : s.games
  totals[i]++
  if (!isCasual(s) && totals[i] > s.rules.bestOf / 2) {
    s.status = 'awaiting_confirmation'
    s.winner = s.teams[i].id
  }
  s.provenance = 'rally'
}
function setGame(s, i) {
  s.games[i]++
  s.points = [0, 0]
  s.server = nextServer(s, s.server)
  s.serviceBox = 'right'
  if (won(s.games, i, 6)) {
    finishSegment(s, i, s.games, 'set')
    s.games = [0, 0]
  } else if (s.games.every((n) => n === s.rules.tieBreakAt)) {
    s.tieBreak = true
    s.tieBreakInitialServer = s.server
  }
}
function racketRally(s, i) {
  s.points[i]++
  if (s.tieBreak) {
    const total = s.points[0] + s.points[1]
    if (won(s.points, i, s.rules.tieBreakTo)) {
      s.games[i]++
      finishSegment(s, i, s.games, 'set')
      s.segments.at(-1).tieBreak = [...s.points]
      s.games = [0, 0]
      s.points = [0, 0]
      s.tieBreak = false
      s.server = nextServer(s, s.tieBreakInitialServer)
      s.tieBreakInitialServer = null
      s.serviceBox = 'right'
    } else {
      s.server = nextServer(
        s,
        s.tieBreakInitialServer,
        Math.floor((total + 1) / 2)
      )
      s.serviceBox = total % 2 === 0 ? 'right' : 'left'
    }
  } else if (won(s.points, i, 4, s.rules.deuce === 'golden_point' ? 1 : 2)) {
    setGame(s, i)
  } else {
    // Canonical deuce avoids unbounded point counters while preserving undo in events.
    if (s.points.every((n) => n >= 4) && s.points[0] === s.points[1])
      s.points = [3, 3]
    s.serviceBox = (s.points[0] + s.points[1]) % 2 === 0 ? 'right' : 'left'
  }
}
function pointGameRally(s, i) {
  const serving = teamOf(s, s.server)
  if (s.rules.sport === 'pickleball' && serving !== i) {
    if (s.teams[0].players.length === 2 && s.serverNumber === 1) {
      s.server = s.teams[serving].players.find((p) => p !== s.server)
      s.serverNumber = 2
    } else {
      s.serverNumber = 1
      s.server =
        s.teams[0].players.length === 2
          ? s.positions[i][0]
          : s.teams[i].players[0]
    }
  } else {
    s.points[i]++
    if (s.rules.sport === 'pickleball' && s.teams[0].players.length === 2)
      s.positions[i].reverse()
    if (s.rules.sport === 'squash') {
      // A hand-out winner chooses either box; do not fabricate their choice.
      if (serving !== i) {
        s.server = s.teams[i].players[0]
        s.serviceBox = null
      } else s.serviceBox = s.serviceBox === 'right' ? 'left' : 'right'
    }
    if (won(s.points, i, s.rules.pointsTo)) {
      finishSegment(s, i, s.points, 'game')
      s.points = [0, 0]
      if (s.rules.sport === 'pickleball') {
        if (isCasual(s) && s.gameInitialServer === null) {
          s.server = null
          s.serviceOrder = []
          s.serviceBox = null
          s.positions = null
          s.serviceKnown = false
          return
        }
        // Starting side alternates each game. Initial partners retain their configured order.
        const firstTeam = (teamOf(s, s.gameInitialServer) + 1) % 2
        s.server = s.teams[firstTeam].players[0]
        s.gameInitialServer = s.server
        s.serverNumber = s.teams[0].players.length === 2 ? 2 : 1
        s.positions = s.teams.map((t) => [t.players[0], t.players[1] || null])
      } else {
        s.server = s.teams[i].players[0]
        s.serviceBox = null
      }
    }
  }
  if (s.rules.sport === 'pickleball') {
    const t = teamOf(s, s.server)
    s.serviceBox =
      s.teams[0].players.length === 2
        ? s.positions[t][0] === s.server
          ? 'right'
          : 'left'
        : s.points[t] % 2 === 0
          ? 'right'
          : 'left'
  }
}
export function reduceRacquet(state, operation) {
  const s = copy(state)
  validateRules(s.rules)
  if (!operation || typeof operation.type !== 'string')
    fail('invalid_operation')
  if (
    ['direct_score', 'finish_session', 'configure_service'].includes(
      operation.type
    )
  ) {
    if (!isCasual(s)) fail('unsupported_operation')
    if (
      !['in_progress', 'paused'].includes(s.status) &&
      !(operation.type === 'direct_score' && s.status === 'session_saved')
    )
      fail('match_not_active')
    if (operation.type === 'finish_session') {
      s.status = 'session_saved'
      s.closure = 'saved'
      s.sportingCompletion = false
      s.winner = null
      s.stoppedSegment = {
        kind: ['tennis', 'padel'].includes(s.rules.sport) ? 'set' : 'game',
        score: [
          ...(['tennis', 'padel'].includes(s.rules.sport) ? s.games : s.points)
        ],
        points: [...s.points],
        completed: false,
        stoppedEarly: true
      }
      return s
    }
    if (operation.type === 'configure_service') {
      const seed = createRacquetState({
        rules: s.rules,
        teams: s.teams,
        serviceOrder: operation.serviceOrder,
        initialServer: operation.server,
        initialBox: s.rules.sport === 'squash' ? operation.serviceBox : 'right'
      })
      if (operation.server == null || operation.serviceOrder == null)
        fail('service_information_required')
      Object.assign(s, {
        serviceOrder: seed.serviceOrder,
        server: seed.server,
        gameInitialServer: s.summaryOnly ? null : seed.server,
        serviceBox: operation.serviceBox,
        positions: seed.positions,
        serverNumber: seed.serverNumber,
        serviceKnown: true
      })
      if (s.rules.sport === 'pickleball') {
        const configured = reduceRacquet(s, {
          type: 'correct_current',
          points: s.points,
          server: operation.server,
          serviceBox: operation.serviceBox,
          serverNumber: operation.serverNumber,
          positions: operation.positions
        })
        return {
          ...configured,
          summaryOnly: s.summaryOnly,
          provenance: s.provenance
        }
      }
      if (['tennis', 'padel'].includes(s.rules.sport)) {
        s.serviceBox = (s.points[0] + s.points[1]) % 2 === 0 ? 'right' : 'left'
        s.tieBreakInitialServer = s.tieBreak ? s.server : null
        if (s.tieBreak)
          s.server = nextServer(
            s,
            s.tieBreakInitialServer,
            Math.floor((s.points[0] + s.points[1] + 1) / 2)
          )
      }
      return s
    }
    if (operation.discardFollowing !== true)
      fail('dependent_history_requires_resolution')
    if (!Array.isArray(operation.segments) || operation.segments.length > 100)
      fail('invalid_segment_score')
    const racket = ['tennis', 'padel'].includes(s.rules.sport)
    const previousStatus =
      s.status === 'session_saved' ? 'in_progress' : s.status
    s.status = 'in_progress'
    s.closure = 'open'
    delete s.stoppedSegment
    s.segments = []
    s.games = [0, 0]
    s.sets = [0, 0]
    s.points = [0, 0]
    s.tieBreak = false
    for (const [index, segment] of operation.segments.entries()) {
      if (!pair(segment.score) || typeof segment.completed !== 'boolean')
        fail('invalid_segment_score')
      if (!segment.completed) {
        if (index !== operation.segments.length - 1)
          fail('unfinished_segment_must_be_last')
        const score = segment.score
        if (racket) {
          if (
            score.some((n) => n > 6) ||
            score.some((_, i) => won(score, i, 6))
          )
            fail('mark_segment_completed')
          s.games = [...score]
          s.tieBreak = score.every((n) => n === 6)
        } else {
          if (score.some((_, i) => won(score, i, s.rules.pointsTo)))
            fail('mark_segment_completed')
          s.points = [...score]
        }
      } else {
        // Reuse the exact completed-segment validator. This creates one summary,
        // never fictional rallies, service or statistics.
        const order = s.teams[0].players.flatMap((p, i) => [
          p,
          s.teams[1].players[i]
        ])
        s.serviceOrder = order
        const next = reduceRacquet(s, {
          type: 'summary_segment',
          score: segment.score,
          server: order[0],
          serviceBox: 'right'
        })
        Object.assign(s, next)
      }
    }
    if (operation.currentPoints != null) {
      if (!racket || !pair(operation.currentPoints)) fail('invalid_point_score')
      const points = operation.currentPoints
      if (
        s.tieBreak
          ? points.some((_, i) => won(points, i, s.rules.tieBreakTo))
          : points.some((_, i) => won(points, i, 4)) ||
            Math.max(...points) > 4 ||
            points.every((n) => n >= 4)
      )
        fail('invalid_point_score')
      s.points = [...points]
    }
    s.server = null
    s.serviceOrder = []
    s.serviceBox = null
    s.positions = null
    s.gameInitialServer = null
    s.tieBreakInitialServer = null
    s.serviceKnown = false
    s.provenance = 'summary'
    s.summaryOnly = true
    s.winner = null
    s.status = previousStatus
    return operation.finishSession === true ? reduceRacquet(s, { type: 'finish_session' }) : s
  }
  if (operation.type === 'pause') {
    if (s.status !== 'in_progress') fail('match_not_active')
    s.status = 'paused'
    return s
  }
  if (operation.type === 'resume') {
    if (s.status !== 'paused') fail('match_not_paused')
    s.status = 'in_progress'
    return s
  }
  if (operation.type === 'confirm_result') {
    if (s.status !== 'awaiting_confirmation' || !s.winner)
      fail('no_result_to_confirm')
    s.status = 'completed'
    return s
  }
  if (['retire', 'abandon', 'unfinished'].includes(operation.type)) {
    if (!['in_progress', 'paused', ...(operation.type === 'abandon' ? ['awaiting_confirmation'] : [])].includes(s.status)) fail('match_not_active')
    s.status = {
      retire: 'retired',
      abandon: 'abandoned',
      unfinished: 'unfinished'
    }[operation.type]
    s.winner = null
    return s
  }
  if (
    s.status !== 'in_progress' &&
    !(
      operation.type === 'replace_segment' &&
      ['completed', 'awaiting_confirmation'].includes(s.status)
    )
  )
    fail('match_not_active')
  if (operation.type === 'replace_segment') {
    s.status = 'in_progress'
    s.winner = null
  }
  if (operation.type === 'service_box') {
    if (
      s.rules.sport !== 'squash' ||
      s.serviceBox !== null ||
      !['left', 'right'].includes(operation.box)
    )
      fail('invalid_service_box')
    s.serviceBox = operation.box
    return s
  }
  if (operation.type === 'rally') {
    if (isCasual(s) && !s.server) fail('service_information_required')
    const i = s.teams.findIndex((t) => t.id === operation.teamId)
    if (i === -1) fail('unknown_team')
    if (s.rules.sport === 'squash' && s.serviceBox === null)
      fail('service_box_required')
    if (['tennis', 'padel'].includes(s.rules.sport)) racketRally(s, i)
    else pointGameRally(s, i)
    return s
  }
  if (operation.type === 'correct_games') {
    if (
      !['tennis', 'padel'].includes(s.rules.sport) ||
      !pair(operation.games) ||
      operation.games.some((n) => n > 6) ||
      operation.games.some((_, i) => won(operation.games, i, 6))
    )
      fail('invalid_game_score')
    if (!s.serviceOrder.includes(operation.server))
      fail('service_information_required')
    s.games = [...operation.games]
    s.points = [0, 0]
    s.server = operation.server
    s.serviceBox = 'right'
    s.tieBreak = s.games.every((n) => n === 6)
    s.tieBreakInitialServer = s.tieBreak ? s.server : null
    s.provenance = 'summary'
    s.summaryOnly = true
    return s
  }
  if (operation.type === 'award_game') {
    if (!['tennis', 'padel'].includes(s.rules.sport) || s.tieBreak)
      fail('use_segment_summary')
    const i = s.teams.findIndex((t) => t.id === operation.teamId)
    if (i < 0) fail('unknown_team')
    s.provenance = 'summary'
    s.summaryOnly = true
    setGame(s, i)
    return s
  }
  if (['summary_segment', 'replace_segment'].includes(operation.type)) {
    const racket = ['tennis', 'padel'].includes(s.rules.sport)
    const score = operation.score
    if (!pair(score) || score[0] === score[1]) fail('invalid_segment_score')
    const i = score[0] > score[1] ? 0 : 1,
      loser = score[1 - i],
      winner = score[i]
    if (
      racket
        ? !(
            (winner === 6 && loser <= 4) ||
            (winner === 7 && [5, 6].includes(loser))
          )
        : !(
            winner >= s.rules.pointsTo &&
            winner - loser >= 2 &&
            (winner === s.rules.pointsTo || winner - loser === 2)
          )
    )
      fail('invalid_segment_score')
    if (
      !s.serviceOrder.includes(operation.server) ||
      !['left', 'right'].includes(operation.serviceBox)
    )
      fail('service_information_required')
    if (operation.type === 'replace_segment') {
      if (
        !Number.isInteger(operation.index) ||
        operation.index < 0 ||
        operation.index >= s.segments.length ||
        operation.discardFollowing !== true
      )
        fail('dependent_history_requires_resolution')
      s.segments = s.segments.slice(0, operation.index)
      s.games = [0, 0]
      s.sets = [0, 0]
      for (const segment of s.segments)
        (racket ? s.sets : s.games)[
          s.teams.findIndex((t) => t.id === segment.winner)
        ]++
    }
    if (
      operation.type === 'summary_segment' &&
      racket &&
      score.some((n, j) => n < s.games[j])
    )
      fail('summary_would_rewrite_games')
    s.provenance = 'summary'
    s.summaryOnly = true
    finishSegment(s, i, score, racket ? 'set' : 'game')
    s.points = [0, 0]
    if (racket) s.games = [0, 0]
    s.tieBreak = false
    s.tieBreakInitialServer = null
    s.server = operation.server
    s.serviceBox = operation.serviceBox
    s.serverNumber =
      s.rules.sport === 'pickleball' && s.teams[0].players.length === 2 ? 2 : 1
    s.gameInitialServer = s.server
    s.positions = s.teams.map((t) => [t.players[0], t.players[1] || null])
    if (s.rules.sport === 'pickleball') {
      if (s.serviceBox !== 'right') fail('invalid_service_box')
      const t = teamOf(s, s.server)
      if (s.teams[0].players.length === 2 && s.positions[t][0] !== s.server)
        s.positions[t].reverse()
    }
    return s
  }
  // Only current segment correction is allowed. Historical edits require a separate
  // resolution workflow; accepting an arbitrary full-state replacement is unsafe.
  if (operation.type === 'correct_current') {
    const { points, server, serverNumber, serviceBox, positions } = operation
    if (
      !pair(points) ||
      !s.serviceOrder.includes(server) ||
      !['left', 'right'].includes(serviceBox)
    )
      fail('invalid_correction')
    if (['tennis', 'padel'].includes(s.rules.sport)) {
      if (
        s.tieBreak
          ? points.some((_, i) => won(points, i, s.rules.tieBreakTo))
          : points.some((_, i) =>
              won(points, i, 4, s.rules.deuce === 'golden_point' ? 1 : 2)
            )
      )
        fail('terminal_score_requires_segment')
      if (
        !s.tieBreak &&
        (Math.max(...points) > 4 || points.every((n) => n >= 4))
      )
        fail('invalid_point_score')
      if (serviceBox !== ((points[0] + points[1]) % 2 === 0 ? 'right' : 'left'))
        fail('invalid_service_box')
      if (
        s.tieBreak &&
        server !==
          nextServer(
            s,
            s.tieBreakInitialServer,
            Math.floor((points[0] + points[1] + 1) / 2)
          )
      )
        fail('invalid_service_order')
      if (!s.tieBreak && server !== s.server) fail('invalid_service_order')
    } else {
      if (points.some((_, i) => won(points, i, s.rules.pointsTo)))
        fail('terminal_score_requires_segment')
      if (s.rules.sport === 'pickleball') {
        if (
          ![1, 2].includes(serverNumber) ||
          (s.teams[0].players.length === 1 && serverNumber !== 1)
        )
          fail('server_number_required')
        if (
          s.teams[0].players.length === 1 &&
          serviceBox !==
            (points[teamOf(s, server)] % 2 === 0 ? 'right' : 'left')
        )
          fail('invalid_service_box')
        if (
          !Array.isArray(positions) ||
          positions.length !== 2 ||
          positions.some(
            (p, i) =>
              !Array.isArray(p) ||
              p.length !== 2 ||
              p.filter(Boolean).length !== s.teams[i].players.length ||
              new Set(p.filter(Boolean)).size !== s.teams[i].players.length ||
              p.filter(Boolean).some((id) => !s.teams[i].players.includes(id))
          )
        )
          fail('court_positions_required')
        if (
          s.teams[0].players.length === 2 &&
          positions[teamOf(s, server)][serviceBox === 'right' ? 0 : 1] !==
            server
        )
          fail('invalid_court_positions')
        s.positions = copy(positions)
        s.serverNumber = serverNumber
      }
    }
    s.points = [...points]
    s.server = server
    s.serviceBox = serviceBox
    s.provenance = 'summary'
    s.summaryOnly = true
    return s
  }
  fail('unsupported_operation')
}
/** Append-only undo targets the latest active scoring operation, not a display value. */
export function reconstructRacquet(initial, events) {
  const active = []
  for (const event of events) {
    if (event.operation.type === 'undo') {
      const index = active.findLastIndex((e) =>
        [
          'direct_score',
          'configure_service',
          'rally',
          'correct_current',
          'correct_games',
          'replace_last_rally',
          'award_game',
          'summary_segment',
          'replace_segment'
        ].includes(e.operation.type)
      )
      if (index === -1 || active[index].id !== event.operation.targetEventId)
        fail('undo_target_changed')
      // A Squash box choice belongs to its rally; lifecycle dependants stay protected.
      if (
        active.slice(index + 1).some((e) => e.operation.type !== 'service_box')
      )
        fail('undo_has_dependants')
      active.splice(index)
    } else active.push(event)
  }
  const replay = (list) =>
    list.reduce((s, e, index) => {
      if (e.operation.type === 'replace_last_rally') {
        const previous = list[index - 1]
        if (
          !previous ||
          previous.operation.type !== 'rally' ||
          previous.id !== e.operation.targetEventId
        )
          fail('correction_target_changed')
        return reduceRacquet(replay(list.slice(0, index - 1)), {
          type: 'rally',
          teamId: e.operation.teamId
        })
      }
      return reduceRacquet(s, e.operation)
    }, copy(initial))
  return replay(active)
}
export function pointLabels(s) {
  if (!['tennis', 'padel'].includes(s.rules.sport) || s.tieBreak)
    return s.points.map(String)
  if (s.points.every((n) => n >= 3))
    return s.points.map((n, i) =>
      n > s.points[1 - i] ? 'AD' : n < s.points[1 - i] ? '40' : '40'
    )
  return s.points.map((n) => ['0', '15', '30', '40'][n])
}

export function activeRacquetEvents(events) {
  const active = []
  for (const e of events) {
    if (e.operation.type === 'undo') {
      const i = active.findIndex((x) => x.id === e.operation.targetEventId)
      if (i >= 0) active.splice(i)
    } else active.push(e)
  }
  return active
}
export function racquetCues(s) {
  if (s.status !== 'in_progress' || s.serviceBox === null || !s.server)
    return []
  const cues = []
  if (
    ['tennis', 'padel'].includes(s.rules.sport) &&
    !s.tieBreak &&
    s.points[0] === 3 &&
    s.points[1] === 3
  )
    cues.push({ key: 'deuce' })
  for (const t of s.teams) {
    const next = reduceRacquet(s, { type: 'rally', teamId: t.id })
    if (next.winner) cues.push({ key: 'matchPoint', teamId: t.id })
    else if (
      next.segments.length > s.segments.length ||
      JSON.stringify(next.games) !== JSON.stringify(s.games)
    )
      cues.push({ key: 'gamePoint', teamId: t.id })
  }
  return cues
}

export function lastRacquetScoringEvent(events) {
  const active = activeRacquetEvents(events),
    types = [
      'direct_score',
      'configure_service',
      'rally',
      'correct_current',
      'correct_games',
      'replace_last_rally',
      'award_game',
      'summary_segment',
      'replace_segment'
    ]
  const index = active.findLastIndex((e) => types.includes(e.operation.type))
  return index >= 0 &&
    active.slice(index + 1).every((e) => e.operation.type === 'service_box')
    ? active[index]
    : null
}
